/**
 * Parser - TypeScript spec file parser for Brick modules
 * Parses spec.brick-module.ts files and extracts method signatures, types, and metadata
 */

import fs from "fs-extra";
import pc from "picocolors";
import { Project, type SourceFile as TSMorphSourceFile } from "ts-morph";
import ts from "typescript";
import { BrickCodegenError, type ErrorContext, wrapError } from "./errors";
import type { ComponentConfig } from "./main-index";
import type { ScannedModule } from "./scanner";
import { createTypeAnalyzer, type TypeAnalyzer } from "./utils/type-analysis";

export interface ParsedModule {
  name: string;
  moduleName: string;
  version: string;
  specPath: string;
  constants: ModuleConstant[];
  methods: ModuleMethod[];
  events?: ModuleEvent[];
  sourceModule: ScannedModule;
  interfaceDefinitions: InterfaceDefinition[];
}

export interface ModuleMethod {
  name: string;
  params: MethodParameter[];
  returnType: string;
  isAsync: boolean;
  isSync: boolean;
  signature: string;
  jsDocComment?: string;
  deprecated?: boolean;
}

export interface MethodParameter {
  name: string;
  type: string;
  optional: boolean;
  defaultValue?: any;
  jsDocComment?: string;
  /** If parameter is a function type, structured representation */
  functionType?: {
    paramTypes: string[];
    returnType: string;
    /**
     * For each function parameter, whether it was declared as a rest parameter ("...args").
     * This helps platform generators distinguish between a single array argument vs. TS rest.
     */
    paramRestFlags?: boolean[];
  };
  /** True if parameter is a function/callback */
  isFunction?: boolean;
}

export interface ModuleConstant {
  name: string;
  type: string;
  value?: any;
  readonly: boolean;
  jsDocComment?: string;
}

export interface ModuleEvent {
  name: string;
  payload?: string;
  jsDocComment?: string;
  /** Source file path where this event was defined */
  sourceFile?: string;
  /** Line number in source file */
  sourceLine?: number;
  /** Column number in source file */
  sourceColumn?: number;
}

export interface InterfaceDefinition {
  name: string;
  properties: InterfaceProperty[];
  jsDocComment?: string;
}

export interface InterfaceProperty {
  name: string;
  type: string;
  optional: boolean;
  jsDocComment?: string;
}

export class Parser {
  private config: ComponentConfig;
  private interfaceDefinitions: Map<string, ts.InterfaceDeclaration> =
    new Map();
  private generatedInterfaces: Map<string, InterfaceDefinition> = new Map();
  private typeAnalyzer: TypeAnalyzer;
  private morphProject: Project | null = null;
  private morphSourceFile: TSMorphSourceFile | null = null;
  private currentSourceFile: ts.SourceFile | null = null;
  private currentModuleName: string | null = null;

  constructor(config: ComponentConfig) {
    this.config = config;
    this.typeAnalyzer = createTypeAnalyzer();
  }

  /**
   * Extracts line and column information from a TypeScript node
   */
  private getNodeLocation(node: ts.Node): { line: number; column: number } {
    if (!this.currentSourceFile) {
      return { line: 0, column: 0 };
    }
    const { line, character } =
      this.currentSourceFile.getLineAndCharacterOfPosition(
        node.getStart(this.currentSourceFile)
      );
    return { line: line + 1, column: character + 1 }; // Convert to 1-indexed
  }

  /**
   * Creates error context from current parsing state
   */
  private createErrorContext(
    node?: ts.Node,
    memberName?: string,
    parameterName?: string
  ): ErrorContext {
    const context: ErrorContext = {
      filePath: this.currentSourceFile?.fileName,
      moduleName: this.currentModuleName || undefined,
      memberName,
      parameterName,
    };

    if (node && this.currentSourceFile) {
      const location = this.getNodeLocation(node);
      context.line = location.line;
      context.column = location.column;
    }

    return context;
  }

  /**
   * Parses a module specification file
   */
  async parseModuleSpec(module: ScannedModule): Promise<ParsedModule[]> {
    try {
      this.log(`📝 Parsing spec file: ${module.specPath}`);

      // Clear interface definitions for this module
      this.interfaceDefinitions.clear();
      this.generatedInterfaces.clear();

      // Initialize ts-morph Project for semantic type analysis
      this.morphProject = new Project({
        skipAddingFilesFromTsConfig: true,
        compilerOptions: {
          target: ts.ScriptTarget.Latest,
        },
      });
      this.morphSourceFile = this.morphProject.addSourceFileAtPath(
        module.specPath
      );

      const specContent = await fs.readFile(module.specPath, "utf8");
      const sourceFile = this.createSourceFile(module.specPath, specContent);

      // Store current source file for error context
      this.currentSourceFile = sourceFile;

      const parsedModules = this.extractModuleInfo(sourceFile, module);

      // Validate each parsed module
      for (const parsed of parsedModules) {
        // Update current module name for better error context
        this.currentModuleName = parsed.moduleName;
        this.validateParsedModule(parsed);
      }

      this.log(
        `✅ Successfully parsed ${
          parsedModules.length
        } modules with total ${parsedModules.reduce(
          (sum, m) => sum + m.methods.length,
          0
        )} methods`
      );

      return parsedModules;
    } catch (error) {
      // Wrap error with context
      if (error instanceof BrickCodegenError) {
        throw error;
      }

      throw wrapError(error, {
        filePath: module.specPath,
        moduleName: module.name,
      });
    } finally {
      // Clean up
      this.morphProject = null;
      this.morphSourceFile = null;
      this.currentSourceFile = null;
      this.currentModuleName = null;
    }
  }

  /**
   * Creates TypeScript source file
   */
  private createSourceFile(
    fileName: string,
    sourceText: string
  ): ts.SourceFile {
    try {
      return ts.createSourceFile(
        fileName,
        sourceText,
        ts.ScriptTarget.Latest,
        true
      );
    } catch (error) {
      throw new BrickCodegenError(
        "TypeScript parsing failed. Please check the syntax of your .brick.ts file.",
        { filePath: fileName },
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Extracts module information from source file
   */
  private extractModuleInfo(
    sourceFile: ts.SourceFile,
    sourceModule: ScannedModule
  ): ParsedModule[] {
    const modules: ParsedModule[] = [];
    const modulesByName = new Map<string, ParsedModule>();

    // First pass: collect all interface definitions
    const extractedInterfaces: InterfaceDefinition[] = [];
    ts.forEachChild(sourceFile, (node) => {
      if (ts.isInterfaceDeclaration(node)) {
        this.interfaceDefinitions.set(node.name.text, node);
        // Extract interface definition from AST
        const interfaceDef = this.extractInterfaceDefinition(node);
        if (
          interfaceDef &&
          !interfaceDef.name.includes("ModuleSpec") &&
          !interfaceDef.name.includes("Events")
        ) {
          extractedInterfaces.push(interfaceDef);
        }
      }
    });

    // Second pass: process module specifications
    ts.forEachChild(sourceFile, (node) => {
      if (ts.isInterfaceDeclaration(node) && this.isModuleSpecInterface(node)) {
        const tempResult: ParsedModule = {
          name: sourceModule.name,
          moduleName: "",
          version: sourceModule.version,
          specPath: sourceModule.specPath,
          constants: [],
          methods: [],
          events: [],
          sourceModule,
          interfaceDefinitions: extractedInterfaces,
        };

        this.processInterface(node, tempResult);

        if (tempResult.moduleName) {
          modulesByName.set(tempResult.moduleName, tempResult);
        }
      }
    });

    // Convert map to array
    modules.push(...modulesByName.values());

    // Add generated interfaces to each module
    for (const module of modules) {
      module.interfaceDefinitions.push(...this.generatedInterfaces.values());
    }

    // If no modules were found with explicit names, create a default one
    if (modules.length === 0) {
      const defaultModule: ParsedModule = {
        name: sourceModule.name,
        moduleName: this.capitalizeFirst(sourceModule.name.replace(/-/g, "")),
        version: sourceModule.version,
        specPath: sourceModule.specPath,
        constants: [],
        methods: [],
        events: [],
        sourceModule,
        interfaceDefinitions: extractedInterfaces,
      };

      // Process all nodes again for the default module
      this.visitNode(sourceFile, defaultModule);

      // Add generated interfaces to default module
      defaultModule.interfaceDefinitions.push(
        ...this.generatedInterfaces.values()
      );

      modules.push(defaultModule);
    }

    return modules;
  }

  /**
   * Visits a TypeScript AST node
   */
  private visitNode(node: ts.Node, result: ParsedModule): void {
    if (ts.isInterfaceDeclaration(node)) {
      // Collect interface definitions for type resolution
      this.interfaceDefinitions.set(node.name.text, node);
      this.processInterface(node, result);
    } else if (ts.isTypeAliasDeclaration(node)) {
      this.processTypeAlias(node, result);
    } else if (ts.isVariableStatement(node)) {
      this.processVariableStatement(node, result);
    }

    // Recursively visit child nodes
    ts.forEachChild(node, (child) => this.visitNode(child, result));
  }

  /**
   * Processes interface declaration
   */
  private processInterface(
    node: ts.InterfaceDeclaration,
    result: ParsedModule
  ): void {
    if (!this.isModuleSpecInterface(node)) {
      return;
    }

    const interfaceName = node.name.text;
    this.log(`Processing interface: ${interfaceName}`);

    // Extract module name from interface name if not already set
    if (!result.moduleName) {
      result.moduleName = this.extractModuleNameFromInterface(interfaceName);
    }

    for (const member of node.members) {
      if (ts.isPropertySignature(member)) {
        this.extractProperty(member, result);
      } else if (ts.isMethodSignature(member)) {
        this.extractMethod(member, result);
      }
    }
  }

  /**
   * Processes type alias declaration
   */
  private processTypeAlias(
    node: ts.TypeAliasDeclaration,
    result: ParsedModule
  ): void {
    if (!this.isModuleSpecType(node)) {
      return;
    }

    if (ts.isTypeLiteralNode(node.type)) {
      for (const member of node.type.members) {
        if (ts.isPropertySignature(member)) {
          this.extractProperty(member, result);
        } else if (ts.isMethodSignature(member)) {
          this.extractMethod(member, result);
        }
      }
    }
  }

  /**
   * Processes variable statement
   */
  private processVariableStatement(
    node: ts.VariableStatement,
    result: ParsedModule
  ): void {
    if (this.hasExportModifier(node)) {
      for (const declaration of node.declarationList.declarations) {
        if (ts.isIdentifier(declaration.name) && declaration.initializer) {
          const constant: ModuleConstant = {
            name: declaration.name.text,
            type: this.inferTypeFromValue(declaration.initializer),
            value: this.extractLiteralValue(declaration.initializer),
            readonly: (node.declarationList.flags & ts.NodeFlags.Const) !== 0,
            jsDocComment: this.extractJSDocComment(node),
          };

          result.constants.push(constant);
        }
      }
    }
  }

  /**
   * Checks if an interface is a module spec interface
   */
  private isModuleSpecInterface(node: ts.InterfaceDeclaration): boolean {
    const name = node.name.text;
    return (
      name.includes("Spec") ||
      name.includes("Module") ||
      name.includes("Interface")
    );
  }

  /**
   * Extracts module name from interface name
   * Example: "CalculatorModuleSpec" -> "Calculator"
   */
  private extractModuleNameFromInterface(interfaceName: string): string {
    // Remove common suffixes to get the module name
    const suffixes = ["ModuleSpec", "Module", "Spec", "Interface", "Type"];

    for (const suffix of suffixes) {
      if (interfaceName.endsWith(suffix)) {
        return interfaceName.slice(0, -suffix.length);
      }
    }

    // If no suffix matches, return the interface name as is
    return interfaceName;
  }

  /**
   * Checks if a type alias is a module spec type
   */
  private isModuleSpecType(node: ts.TypeAliasDeclaration): boolean {
    const name = node.name.text;
    return (
      name.includes("Spec") || name.includes("Module") || name.includes("Type")
    );
  }

  /**
   * Checks if node has export modifier
   */
  private hasExportModifier(node: ts.Node): boolean {
    if (ts.canHaveModifiers(node)) {
      const modifiers = ts.getModifiers(node);
      return (
        modifiers?.some(
          (modifier) => modifier.kind === ts.SyntaxKind.ExportKeyword
        ) ?? false
      );
    }
    return false;
  }

  /**
   * Extracts property from interface/type
   */
  private extractProperty(
    node: ts.PropertySignature,
    result: ParsedModule
  ): void {
    if (!node.name || !ts.isIdentifier(node.name)) return;

    const propertyName = node.name.text;
    const isReadonly = ts.canHaveModifiers(node)
      ? ts
          .getModifiers(node)
          ?.some((mod) => mod.kind === ts.SyntaxKind.ReadonlyKeyword) ?? false
      : false;
    const typeAnnotation = this.getTypeString(node.type);

    // Detect EventEmitter-style events: readonly onXxx: CodegenTypes.EventEmitter<Payload>
    if (
      propertyName.startsWith("on") &&
      propertyName.length > 2 &&
      propertyName[2] === propertyName[2]?.toUpperCase() &&
      node.type &&
      ts.isTypeReferenceNode(node.type)
    ) {
      const typeRef = node.type;
      const t = typeRef.typeName;
      const isEventEmitterType =
        (ts.isQualifiedName(t) &&
          ts.isIdentifier(t.left) &&
          t.left.text === "CodegenTypes" &&
          ts.isIdentifier(t.right) &&
          t.right.text === "EventEmitter") ||
        (ts.isIdentifier(t) && t.text === "EventEmitter");

      if (isEventEmitterType) {
        if (!result.events) result.events = [];

        const eventBase = propertyName.slice(2);
        const eventName =
          eventBase.charAt(0).toLowerCase() + eventBase.slice(1);
        const payloadType =
          typeRef.typeArguments && typeRef.typeArguments[0]
            ? this.getTypeString(typeRef.typeArguments[0])
            : undefined;

        result.events.push({
          name: eventName,
          payload: payloadType,
          jsDocComment: this.extractJSDocComment(node),
        });

        return; // handled as event, do not treat as constant
      }
    }

    // Check if this is a function type property that should be converted to a method
    if (node.type && ts.isFunctionTypeNode(node.type)) {
      this.convertPropertyToMethod(node, result);
      return;
    }

    // Handle special properties
    switch (propertyName) {
      case "moduleName":
        if (
          node.type &&
          ts.isLiteralTypeNode(node.type) &&
          ts.isStringLiteral(node.type.literal)
        ) {
          result.moduleName = node.type.literal.text;
        }
        break;

      case "version":
        // Extract version if specified
        break;

      case "constants":
        // Deprecated: Extract constants object structure (legacy support)
        if (node.type && ts.isTypeLiteralNode(node.type)) {
          this.extractConstantsFromType(node.type, result);
        }
        break;

      default:
        // Regular properties are no longer treated as constants
        // Constants must be defined via getConstants() method
        break;
    }
  }

  /**
   * Extracts method from interface
   */
  private extractMethod(node: ts.MethodSignature, result: ParsedModule): void {
    if (!node.name || !ts.isIdentifier(node.name)) return;

    const methodName = node.name.text;

    // Special handling for getConstants method
    if (methodName === "getConstants") {
      if (node.type && ts.isTypeLiteralNode(node.type)) {
        // Extract constants from the return type object literal
        this.extractConstantsFromType(node.type, result);
      }
      return; // Don't add getConstants as a method
    }

    const params = this.extractParameters(node.parameters, methodName);
    const returnType = this.getTypeStringWithMethodContext(
      node.type,
      methodName,
      "Response"
    );

    // Use semantic type analysis if type node is available, otherwise fall back to string check
    const isAsync = node.type
      ? this.isPromiseTypeNode(node.type)
      : this.isPromiseType(returnType);

    const method: ModuleMethod = {
      name: methodName,
      params,
      returnType,
      isAsync,
      isSync: !isAsync,
      signature: this.buildMethodSignature(methodName, params, returnType),
      jsDocComment: this.extractJSDocComment(node),
      deprecated: this.isDeprecated(node),
    };

    result.methods.push(method);
  }

  /**
   * Converts a property with function type to a method
   * Handles properties like: methodName: (param: Type) => ReturnType
   */
  private convertPropertyToMethod(
    node: ts.PropertySignature,
    result: ParsedModule
  ): void {
    if (!node.name || !ts.isIdentifier(node.name)) return;
    if (!node.type || !ts.isFunctionTypeNode(node.type)) return;

    const methodName = node.name.text;
    const functionType = node.type as ts.FunctionTypeNode;

    // Extract parameters from function type
    const params = this.extractParameters(functionType.parameters, methodName);
    const returnType = this.getTypeStringWithMethodContext(
      functionType.type,
      methodName,
      "Response"
    );

    // Check if return type is Promise (async method)
    const isAsync = functionType.type
      ? this.isPromiseTypeNode(functionType.type)
      : this.isPromiseType(returnType);

    const method: ModuleMethod = {
      name: methodName,
      params,
      returnType,
      isAsync,
      isSync: !isAsync,
      signature: this.buildMethodSignature(methodName, params, returnType),
      jsDocComment: this.extractJSDocComment(node),
      deprecated: this.isDeprecated(node),
    };

    result.methods.push(method);
  }

  /**
   * Extracts constants from a type literal
   */
  private extractConstantsFromType(
    typeLiteral: ts.TypeLiteralNode,
    result: ParsedModule
  ): void {
    for (const member of typeLiteral.members) {
      if (
        ts.isPropertySignature(member) &&
        member.name &&
        ts.isIdentifier(member.name)
      ) {
        const constant: ModuleConstant = {
          name: member.name.text,
          type: this.getTypeString(member.type),
          readonly: ts.canHaveModifiers(member)
            ? ts
                .getModifiers(member)
                ?.some((mod) => mod.kind === ts.SyntaxKind.ReadonlyKeyword) ??
              true
            : true,
          jsDocComment: this.extractJSDocComment(member),
        };

        result.constants.push(constant);
      }
    }
  }

  /**
   * Extracts supported events from array literal
   */
  private extractSupportedEvents(
    node: ts.PropertySignature,
    result: ParsedModule
  ): void {
    if (!result.events) {
      result.events = [];
    }

    if (node.type && ts.isTupleTypeNode(node.type)) {
      // Handle tuple type: ['event1', 'event2']
      for (const element of node.type.elements) {
        if (
          ts.isLiteralTypeNode(element) &&
          ts.isStringLiteral(element.literal)
        ) {
          const eventName = element.literal.text;
          const location = this.getNodeLocation(element);

          result.events.push({
            name: eventName,
            jsDocComment: this.extractJSDocComment(node),
            sourceFile: this.currentSourceFile?.fileName,
            sourceLine: location.line,
            sourceColumn: location.column,
          });
        }
      }
    } else if (node.type && ts.isArrayTypeNode(node.type)) {
      // Handle array type with literal elements
      const elementType = node.type.elementType;
      if (
        ts.isLiteralTypeNode(elementType) &&
        ts.isStringLiteral(elementType.literal)
      ) {
        const location = this.getNodeLocation(elementType);

        // This is for single event type arrays
        result.events.push({
          name: elementType.literal.text,
          jsDocComment: this.extractJSDocComment(node),
          sourceFile: this.currentSourceFile?.fileName,
          sourceLine: location.line,
          sourceColumn: location.column,
        });
      }
    }

    this.log(`Extracted ${result.events.length} supported events`);
  }

  /**
   * Extracts parameters from method signature
   */
  private extractParameters(
    parameters: ts.NodeArray<ts.ParameterDeclaration>,
    methodName?: string
  ): MethodParameter[] {
    const params: MethodParameter[] = [];

    for (const param of parameters) {
      if (ts.isIdentifier(param.name)) {
        // Use context-aware type extraction if method name is provided
        const typeString = methodName
          ? this.getTypeStringWithMethodContext(
              param.type,
              methodName,
              param.name.text
            )
          : this.getTypeString(param.type);

        let functionType:
          | {
              paramTypes: string[];
              returnType: string;
              paramRestFlags?: boolean[];
            }
          | undefined;
        let isFunction = false;

        if (param.type && ts.isFunctionTypeNode(param.type)) {
          const fnParamTypes = param.type.parameters.map((p) =>
            this.getTypeString(p.type)
          );
          const fnParamRestFlags = param.type.parameters.map(
            (p) => !!p.dotDotDotToken
          );

          const fnReturnType = this.getTypeString(param.type.type);
          functionType = {
            paramTypes: fnParamTypes,
            returnType: fnReturnType,
            paramRestFlags: fnParamRestFlags,
          };
          isFunction = true;
        }

        params.push({
          name: param.name.text,
          type: typeString,
          optional: !!param.questionToken,
          defaultValue: param.initializer
            ? this.extractDefaultValue(param.initializer)
            : undefined,
          jsDocComment: this.extractJSDocComment(param),
          functionType,
          isFunction,
        });
      }
    }

    return params;
  }

  /**
   * Extracts default value from expression
   */
  private extractDefaultValue(node: ts.Expression): any {
    if (ts.isNumericLiteral(node)) return Number(node.text);
    if (ts.isStringLiteral(node)) return node.text;
    if (node.kind === ts.SyntaxKind.TrueKeyword) return true;
    if (node.kind === ts.SyntaxKind.FalseKeyword) return false;
    if (node.kind === ts.SyntaxKind.NullKeyword) return null;
    if (node.kind === ts.SyntaxKind.UndefinedKeyword) return undefined;
    return undefined;
  }

  /**
   * Gets type string from type node
   */
  private getTypeString(typeNode?: ts.TypeNode): string {
    if (!typeNode) return "any";

    return this.typeToString(typeNode);
  }

  /**
   * Generates an interface name for nested object types
   */
  private generateNestedInterfaceName(
    parentName: string,
    propertyName: string
  ): string {
    // Capitalize first letter of property name
    const capitalizedProperty =
      propertyName.charAt(0).toUpperCase() + propertyName.slice(1);
    return `${parentName}${capitalizedProperty}`;
  }

  /**
   * Extracts nested object type as interface definition
   */
  private extractNestedInterface(
    typeLiteral: ts.TypeLiteralNode,
    parentName: string,
    propertyName: string
  ): string {
    const interfaceName = this.generateNestedInterfaceName(
      parentName,
      propertyName
    );

    // Check if we already generated this interface
    if (this.generatedInterfaces.has(interfaceName)) {
      return interfaceName;
    }

    const properties: InterfaceProperty[] = [];

    for (const member of typeLiteral.members) {
      if (
        ts.isPropertySignature(member) &&
        member.name &&
        ts.isIdentifier(member.name)
      ) {
        const propName = member.name.text;
        let propType: string;

        if (member.type && ts.isTypeLiteralNode(member.type)) {
          // Recursively handle nested objects
          propType = this.extractNestedInterface(
            member.type,
            interfaceName,
            propName
          );
        } else {
          propType = this.getTypeString(member.type);
        }

        const optional = !!member.questionToken;

        properties.push({
          name: propName,
          type: propType,
          optional,
          jsDocComment: this.extractJSDocComment(member),
        });
      }
    }

    // Store the generated interface
    const interfaceDef: InterfaceDefinition = {
      name: interfaceName,
      properties,
      jsDocComment: `Auto-generated interface for ${parentName}.${propertyName}`,
    };

    this.generatedInterfaces.set(interfaceName, interfaceDef);
    return interfaceName;
  }

  /**
   * Converts TypeScript type to string representation
   */
  private typeToString(
    type: ts.TypeNode,
    parentContext?: { parentName: string; propertyName: string }
  ): string {
    switch (type.kind) {
      case ts.SyntaxKind.StringKeyword:
        return "string";
      case ts.SyntaxKind.NumberKeyword:
        return "number";
      case ts.SyntaxKind.BooleanKeyword:
        return "boolean";
      case ts.SyntaxKind.VoidKeyword:
        return "void";
      case ts.SyntaxKind.AnyKeyword:
        return "any";
      case ts.SyntaxKind.UnknownKeyword:
        return "unknown";
      case ts.SyntaxKind.NullKeyword:
        return "null";
      case ts.SyntaxKind.UndefinedKeyword:
        return "undefined";
    }

    if (ts.isArrayTypeNode(type)) {
      const elementType = this.typeToString(type.elementType);
      return `${elementType}[]`;
    }

    if (ts.isTypeReferenceNode(type) && ts.isIdentifier(type.typeName)) {
      const typeName = type.typeName.text;

      if (typeName === "Promise" && type.typeArguments?.[0]) {
        return `Promise<${this.typeToString(type.typeArguments[0])}>`;
      }

      // Keep original interface types - conversion to Object only happens in TurboModule spec generation

      return typeName;
    }

    if (ts.isLiteralTypeNode(type)) {
      if (ts.isStringLiteral(type.literal)) return `"${type.literal.text}"`;
      if (ts.isNumericLiteral(type.literal)) return type.literal.text;
      if (type.literal.kind === ts.SyntaxKind.TrueKeyword) return "true";
      if (type.literal.kind === ts.SyntaxKind.FalseKeyword) return "false";
    }

    if (ts.isUnionTypeNode(type)) {
      return type.types.map((t) => this.typeToString(t)).join(" | ");
    }

    if (ts.isTypeLiteralNode(type)) {
      // If we have parent context, generate a nested interface
      if (parentContext) {
        return this.extractNestedInterface(
          type,
          parentContext.parentName,
          parentContext.propertyName
        );
      }

      // Otherwise, return the object type as string (fallback)
      const members = type.members
        .map((member) => {
          if (
            ts.isPropertySignature(member) &&
            member.name &&
            ts.isIdentifier(member.name)
          ) {
            const key = member.name.text;
            const valueType = member.type
              ? this.typeToString(member.type)
              : "any";
            const optional = member.questionToken ? "?" : "";
            const readonly =
              ts.canHaveModifiers(member) &&
              ts
                .getModifiers(member)
                ?.some((mod) => mod.kind === ts.SyntaxKind.ReadonlyKeyword)
                ? "readonly "
                : "";
            return `${readonly}${key}${optional}: ${valueType}`;
          }
          return "";
        })
        .filter(Boolean);

      return `{${members.join("; ")}}`;
    }

    // Fallback
    return type.getText?.() || "any";
  }

  /**
   * Gets type string with method context for generating anonymous interfaces
   * Handles anonymous object types in method parameters and return types
   */
  private getTypeStringWithMethodContext(
    typeNode: ts.TypeNode | undefined,
    methodName: string,
    paramOrReturnName: string
  ): string {
    if (!typeNode) return "any";

    // Capitalize method name for interface generation
    const capitalizedMethod =
      methodName.charAt(0).toUpperCase() + methodName.slice(1);

    // Handle anonymous object types directly
    if (ts.isTypeLiteralNode(typeNode)) {
      return this.extractNestedInterface(
        typeNode,
        capitalizedMethod,
        paramOrReturnName
      );
    }

    // Handle Promise<anonymous object>
    if (
      ts.isTypeReferenceNode(typeNode) &&
      ts.isIdentifier(typeNode.typeName) &&
      typeNode.typeName.text === "Promise" &&
      typeNode.typeArguments?.[0]
    ) {
      const innerType = typeNode.typeArguments[0];
      if (ts.isTypeLiteralNode(innerType)) {
        const innerInterface = this.extractNestedInterface(
          innerType,
          capitalizedMethod,
          paramOrReturnName
        );
        return `Promise<${innerInterface}>`;
      }
    }

    // Otherwise use normal type string conversion
    return this.typeToString(typeNode);
  }

  /**
   * Checks if a type is a Promise type (string-based for backward compatibility)
   * For new code, prefer using ts-morph type analysis directly
   */
  private isPromiseType(typeString: string): boolean {
    return typeString.startsWith("Promise<");
  }

  /**
   * Checks if a type node is a Promise using semantic analysis
   */
  private isPromiseTypeNode(typeNode: ts.TypeNode): boolean {
    if (
      ts.isTypeReferenceNode(typeNode) &&
      ts.isIdentifier(typeNode.typeName)
    ) {
      return typeNode.typeName.text === "Promise";
    }
    return false;
  }

  /**
   * Builds method signature string
   */
  private buildMethodSignature(
    name: string,
    params: MethodParameter[],
    returnType: string
  ): string {
    const paramStrings = params.map((param) => {
      const optional = param.optional ? "?" : "";
      return `${param.name}${optional}: ${param.type}`;
    });

    return `${name}(${paramStrings.join(", ")}): ${returnType}`;
  }

  /**
   * Extracts JSDoc comment from node
   * Cleans up asterisk prefixes from each line
   */
  private extractJSDocComment(node: ts.Node): string | undefined {
    const sourceFile = node.getSourceFile();
    const fullText = sourceFile.getFullText();
    const nodeStart = node.getFullStart();
    const nodeEnd = node.getStart();
    const leadingTrivia = fullText.substring(nodeStart, nodeEnd);

    const jsDocMatch = leadingTrivia.match(/\/\*\*([\s\S]*?)\*\//);
    if (jsDocMatch?.[1]) {
      // Clean up the comment by removing leading asterisks and whitespace from each line
      const rawComment = jsDocMatch[1];
      const lines = rawComment.split('\n');
      const cleanedLines = lines
        .map(line => line.replace(/^\s*\*\s?/, '')) // Remove leading whitespace, asterisk, and optional space
        .filter((line, index, arr) => {
          // Remove empty lines at the start and end, but keep them in the middle
          if (index === 0 || index === arr.length - 1) {
            return line.trim().length > 0;
          }
          return true;
        });
      return cleanedLines.join('\n').trim();
    }
    return undefined;
  }

  /**
   * Checks if node is marked as deprecated
   */
  private isDeprecated(node: ts.Node): boolean {
    const jsDoc = this.extractJSDocComment(node);
    return jsDoc?.includes("@deprecated") || false;
  }

  /**
   * Infers type from value expression
   */
  private inferTypeFromValue(node: ts.Expression): string {
    if (ts.isStringLiteral(node)) return "string";
    if (ts.isNumericLiteral(node)) return "number";
    if (
      node.kind === ts.SyntaxKind.TrueKeyword ||
      node.kind === ts.SyntaxKind.FalseKeyword
    )
      return "boolean";
    if (ts.isArrayLiteralExpression(node)) return "any[]";
    if (ts.isObjectLiteralExpression(node)) return "object";
    return "any";
  }

  /**
   * Extracts literal value from expression
   */
  private extractLiteralValue(node: ts.Expression): any {
    if (ts.isStringLiteral(node)) return node.text;
    if (ts.isNumericLiteral(node)) return Number(node.text);
    if (node.kind === ts.SyntaxKind.TrueKeyword) return true;
    if (node.kind === ts.SyntaxKind.FalseKeyword) return false;
    if (node.kind === ts.SyntaxKind.NullKeyword) return null;
    return undefined;
  }

  /**
   * Validates parsed module structure
   */
  private validateParsedModule(parsed: ParsedModule): void {
    if (!parsed.moduleName) {
      throw new BrickCodegenError(
        "Module name not found in spec",
        this.createErrorContext(),
        undefined
      );
    }

    if (parsed.methods.length === 0) {
      throw new BrickCodegenError(
        "No methods found in module spec",
        this.createErrorContext(),
        undefined
      );
    }

    // Validate method names
    const methodNames = new Set<string>();
    for (const method of parsed.methods) {
      if (methodNames.has(method.name)) {
        throw new BrickCodegenError(
          `Duplicate method name: ${method.name}`,
          this.createErrorContext(undefined, method.name),
          undefined
        );
      }
      methodNames.add(method.name);

      if (!this.isValidMethodName(method.name)) {
        throw new BrickCodegenError(
          `Invalid method name: ${method.name}. Method names must start with a letter and contain only letters, numbers, and underscores.`,
          this.createErrorContext(undefined, method.name),
          undefined
        );
      }
    }

    // Validate constant names
    const constantNames = new Set<string>();
    for (const constant of parsed.constants) {
      if (constantNames.has(constant.name)) {
        throw new BrickCodegenError(
          `Duplicate constant name: ${constant.name}`,
          this.createErrorContext(undefined, constant.name),
          undefined
        );
      }
      constantNames.add(constant.name);
    }

    // Validate event names
    if (parsed.events && parsed.events.length > 0) {
      const eventNames = new Set<string>();
      for (const event of parsed.events) {
        if (eventNames.has(event.name)) {
          throw new BrickCodegenError(
            `Duplicate event name: ${event.name}`,
            this.createErrorContext(undefined, event.name),
            undefined
          );
        }
        eventNames.add(event.name);

        if (!this.isValidEventName(event.name)) {
          throw new BrickCodegenError(
            `Invalid event name: ${event.name}. Event names must start with a letter and contain only letters and numbers (camelCase preferred).`,
            this.createErrorContext(undefined, event.name),
            undefined
          );
        }
      }
    }
  }

  /**
   * Validates method name conventions
   */
  private isValidMethodName(name: string): boolean {
    // Must start with letter, can contain letters, numbers, underscores
    return /^[a-zA-Z][a-zA-Z0-9_]*$/.test(name);
  }

  /**
   * Validates event name conventions
   */
  private isValidEventName(name: string): boolean {
    // Must start with letter, can contain letters, numbers, underscores
    // Event names should be camelCase
    return /^[a-zA-Z][a-zA-Z0-9]*$/.test(name);
  }

  /**
   * Validates naming conventions
   */
  validateNamingConventions(parsed: ParsedModule): string[] {
    const warnings: string[] = [];

    // Check module name convention
    if (!this.isCapitalCase(parsed.moduleName)) {
      warnings.push(
        `Module name '${parsed.moduleName}' should be in PascalCase`
      );
    }

    // Check method naming
    for (const method of parsed.methods) {
      if (!this.isCamelCase(method.name)) {
        warnings.push(`Method '${method.name}' should be in camelCase`);
      }

      if (
        method.isAsync &&
        !method.name.startsWith("get") &&
        !method.name.includes("async")
      ) {
        warnings.push(
          `Async method '${method.name}' should indicate async nature in name`
        );
      }
    }

    // Check constant naming
    for (const constant of parsed.constants) {
      if (!this.isConstantCase(constant.name)) {
        warnings.push(`Constant '${constant.name}' should be in CONSTANT_CASE`);
      }
    }

    return warnings;
  }

  /**
   * Validates types in the parsed module
   */
  async validateTypes(parsed: ParsedModule): Promise<string[]> {
    const errors: string[] = [];

    // Basic type validation
    for (const method of parsed.methods) {
      // Check return type
      if (!this.isValidType(method.returnType)) {
        errors.push(
          `Invalid return type '${method.returnType}' in method '${method.name}'`
        );
      }

      // Check parameter types
      for (const param of method.params) {
        if (!this.isValidType(param.type)) {
          errors.push(
            `Invalid parameter type '${param.type}' for '${param.name}' in method '${method.name}'`
          );
        }
      }
    }

    return errors;
  }

  /**
   * Checks if type is valid
   */
  private isValidType(type: string): boolean {
    const validTypes = [
      "string",
      "number",
      "boolean",
      "void",
      "any",
      "unknown",
      "null",
      "undefined",
      "object",
      "Array",
      "Promise",
    ];

    // Simple validation - check if it starts with a known type
    return validTypes.some(
      (validType) =>
        type.includes(validType) || type.match(/^[A-Z][a-zA-Z0-9]*$/) // Custom type names
    );
  }

  private isCapitalCase(str: string): boolean {
    return /^[A-Z][a-zA-Z0-9]*$/.test(str);
  }

  private isCamelCase(str: string): boolean {
    return /^[a-z][a-zA-Z0-9]*$/.test(str);
  }

  private isConstantCase(str: string): boolean {
    return /^[A-Z][A-Z0-9_]*$/.test(str);
  }

  /**
   * Extracts interface definition from TypeScript AST
   */
  private extractInterfaceDefinition(
    node: ts.InterfaceDeclaration
  ): InterfaceDefinition | null {
    const name = node.name.text;
    const properties: InterfaceProperty[] = [];

    for (const member of node.members) {
      if (
        ts.isPropertySignature(member) &&
        member.name &&
        ts.isIdentifier(member.name)
      ) {
        const propName = member.name.text;
        let propType: string;

        // Check if the type is a nested object literal
        if (member.type && ts.isTypeLiteralNode(member.type)) {
          propType = this.extractNestedInterface(member.type, name, propName);
        } else {
          propType = this.getTypeString(member.type);
        }

        const optional = !!member.questionToken;

        properties.push({
          name: propName,
          type: propType,
          optional,
          jsDocComment: this.extractJSDocComment(member),
        });
      }
    }

    return {
      name,
      properties,
      jsDocComment: this.extractJSDocComment(node),
    };
  }

  private capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  private log(...args: any[]): void {
    if (this.config.debug) {
      console.log(pc.gray("[parser]"), ...args);
    }
  }
}
